import { useMemo } from 'react'
import { addDays } from '@gk/contracts'
import { OBSERVATION_KINDS, type ObservationKind, type ObservationRecordV1 } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import type { JobAreaRow, JobRow } from './plan.ts'

/** M16-N: a ground observation as the device holds it (ops.observation, mirrored by both profiles): what was seen, where, when, by whom, and — once a planner
 *  followed it — the job. The pure rules of the "Signaler une observation" screen live here with their tests (CLAUDE.md M13: pure screen logic in data/*.ts). */
export interface ObservationRow {
  id: string; area_id: string; area_version_id: string | null; observed_at: string; observed_local_date: string; kind: ObservationKind; note: string | null
  job_id: string | null; job_linked_at: string | null; entered_at: string; actor_person_id?: string | null
}
export type Observation = Pending<ObservationRow>
export const useObservations = () => useMerged<ObservationRow>('observation', 'ops.observation')

export { OBSERVATION_KINDS }
/** How long an observation with no job counts as "sans suite" on Today's "À traiter" (M16-A reads it here): the plan's 7 days, printed on screen as the rule. */
export const OBSERVATION_FOLLOW_UP_DAYS = 7
/** How many of an area's observations the form page lists under it. */
export const OBSERVATION_LIST_MAX = 30

/** The observations of one area, newest first (ties by id, so two phones' rows of one instant keep one order). */
export function observationsOfArea(rows: readonly Observation[], areaId: string, max = OBSERVATION_LIST_MAX): Observation[] {
  return rows.filter((o) => o.area_id === areaId).sort((a, b) => (a.observed_at < b.observed_at ? 1 : a.observed_at > b.observed_at ? -1 : a.id.localeCompare(b.id))).slice(0, max)
}

/** "n observations sans suite": no job linked, seen within the last `days` local days (today included). */
export function withoutFollowUp(rows: readonly Observation[], today: string, days = OBSERVATION_FOLLOW_UP_DAYS): Observation[] {
  const from = addDays(today, -days)
  return rows.filter((o) => !o.job_id && o.observed_local_date >= from && o.observed_local_date <= today)
}

export interface ObservationForm { areaId: string; kind: ObservationKind | ''; note: string; observedAt?: string }
export type ObservationProblem = 'area' | 'kind' | 'note' | 'noteLong'
/** What the form still needs before "Envoyer" is live (M23 item 6: said next to the disabled button). A note is free, except for "Autre", where it is the
 *  only thing the observation says. The server admits an observation of any kind with no note: this is the form's rule, not the record's. */
export function formProblem(f: ObservationForm): ObservationProblem | null {
  if (!f.areaId) return 'area'
  if (!f.kind) return 'kind'
  if ([...f.note.trim()].length > 300) return 'noteLong'
  if (f.kind === 'other' && !f.note.trim()) return 'note'
  return null
}

/** The payload of observation.record@1 from the form: the note trimmed and dropped when empty, the instant only when the person typed one. */
export function buildObservationPayload(id: string, f: ObservationForm): ObservationRecordV1 {
  if (!f.kind) throw new Error('kind')
  const note = f.note.trim()
  return { observation_id: id, area_id: f.areaId, kind: f.kind, ...(note ? { note } : {}), ...(f.observedAt ? { observed_at: f.observedAt } : {}) }
}

export interface CandidateJob { id: string; title: string; date: string; onArea: boolean }
/** The jobs a planner may name as the follow-up of an observation: the site's jobs that are not cancelled, planned on or after the day it was seen, the
 *  ones on its area first, then by day and title. The server does not require the job to name the area (a scouting job may cover the loop). */
export function candidateJobs(jobs: readonly JobRow[], jobAreas: readonly JobAreaRow[], areaId: string, observedLocalDate: string, titleOf: (j: JobRow) => string): CandidateJob[] {
  const onArea = new Set(jobAreas.filter((ja) => ja.area_id === areaId && !ja.removed_by_command_id).map((ja) => ja.job_id))
  return jobs.filter((j) => j.state !== 'cancelled' && j.scheduled_local_date >= observedLocalDate)
    .map((j) => ({ id: j.id, title: titleOf(j), date: j.scheduled_local_date, onArea: onArea.has(j.id) }))
    .sort((a, b) => Number(b.onArea) - Number(a.onArea) || a.date.localeCompare(b.date) || a.title.localeCompare(b.title))
}

/** The hook the form page uses: the area's observations, live, newest first. */
export function useAreaObservations(areaId: string): Observation[] | undefined {
  const rows = useObservations()
  return useMemo(() => (rows ? observationsOfArea(rows, areaId) : undefined), [rows, areaId])
}
